// ============================================================================
// SectionsStep - Component for managing form sections
// ============================================================================
// Sections represent physical or logical divisions for audit navigation
// (e.g., Kitchen, Salon, Bathroom, Exterior, Storage)
// ============================================================================

import React, { useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Room as RoomIcon
} from '@mui/icons-material';
import { Section } from '../FormBuilder';

interface SectionsStepProps {
  sections: Section[];
  onChange: (sections: Section[]) => void;
}

interface SectionDialogData {
  name: string;
  description: string;
  color: string;
}

const DEFAULT_COLORS = [
  '#2196F3', // Blue
  '#4CAF50', // Green
  '#FF9800', // Orange
  '#F44336', // Red
  '#9C27B0', // Purple
  '#00BCD4', // Cyan
  '#FFEB3B', // Yellow
  '#795548'  // Brown
];

const SectionsStep: React.FC<SectionsStepProps> = ({ sections, onChange }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingSection, setEditingSection] = useState<Section | null>(null);
  const [dialogData, setDialogData] = useState<SectionDialogData>({
    name: '',
    description: '',
    color: '#2196F3'
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // ============================================================================
  // Dialog Management
  // ============================================================================

  const handleOpenDialog = (section?: Section) => {
    if (section) {
      setEditingSection(section);
      setDialogData({
        name: section.name,
        description: section.description || '',
        color: section.color || DEFAULT_COLORS[0]
      });
    } else {
      setEditingSection(null);
      setDialogData({
        name: '',
        description: '',
        color: DEFAULT_COLORS[sections.length % DEFAULT_COLORS.length]
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSection(null);
    setDialogData({ name: '', description: '', color: DEFAULT_COLORS[0] });
    setErrors({});
  };

  // ============================================================================
  // Validation
  // ============================================================================

  const validateSection = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!dialogData.name.trim()) {
      newErrors.name = 'El nombre de la sección es requerido';
    } else if (dialogData.name.trim().length > 150) {
      newErrors.name = 'El nombre no puede exceder 150 caracteres';
    }

    // Check for duplicate names
    const isDuplicate = sections.some(
      s => s.name.toLowerCase() === dialogData.name.trim().toLowerCase() && 
           s.id !== editingSection?.id
    );

    if (isDuplicate) {
      newErrors.name = 'Ya existe una sección con este nombre';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============================================================================
  // CRUD Operations
  // ============================================================================

  const handleSaveSection = () => {
    if (!validateSection()) {
      return;
    }

    if (editingSection) {
      // Update existing section
      onChange(
        sections.map(s =>
          s.id === editingSection.id
            ? {
                ...s,
                name: dialogData.name.trim(),
                description: dialogData.description.trim(),
                color: dialogData.color
              }
            : s
        )
      );
    } else {
      // Create new section
      const newSection: Section = {
        id: Date.now(), // Temporary ID
        name: dialogData.name.trim(),
        description: dialogData.description.trim(),
        order: sections.length,
        color: dialogData.color,
        question_count: 0
      };
      onChange([...sections, newSection]);
    }

    handleCloseDialog();
  };

  const handleDeleteSection = (section: Section) => {
    if (section.question_count && section.question_count > 0) {
      alert(
        `No se puede eliminar la sección "${section.name}" porque tiene ${section.question_count} pregunta(s) asignada(s).\n\n` +
        'Por favor, reasigne o elimine las preguntas primero.'
      );
      return;
    }

    if (
      window.confirm(
        `¿Está seguro de eliminar la sección "${section.name}"?\n\nEsta acción no se puede deshacer.`
      )
    ) {
      const newSections = sections.filter(s => s.id !== section.id);
      // Reorder remaining sections
      const reorderedSections = newSections.map((s, index) => ({
        ...s,
        order: index
      }));
      onChange(reorderedSections);
    }
  };

  // ============================================================================
  // Ordenamiento de Secciones
  // ============================================================================

  const handleMoveUp = (index: number) => {
    if (index === 0) return;

    const newSections = [...sections];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    
    // Update order
    const reorderedSections = newSections.map((s, i) => ({ ...s, order: i }));
    onChange(reorderedSections);
  };

  const handleMoveDown = (index: number) => {
    if (index === sections.length - 1) return;

    const newSections = [...sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    
    // Update order
    const reorderedSections = newSections.map((s, i) => ({ ...s, order: i }));
    onChange(reorderedSections);
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Secciones del Formulario
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Las secciones organizan el formulario por ubicación física o flujo de trabajo durante la auditoría.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Agregar Sección
        </Button>
      </Box>

      {/* Help Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          <strong>Ejemplos de secciones:</strong>
        </Typography>
        <Typography variant="body2" component="div">
          • <strong>Gastronomía:</strong> Exterior, Salón, Barra, Cocina, Baños, Depósito
          <br />
          • <strong>Retail:</strong> Vidriera, Sala de ventas, Caja, Depósito, Área de empleados
          <br />
          • <strong>Auditoría remota:</strong> Apertura, Procesos contables, Cambio de turno, Cierre
        </Typography>
      </Alert>

      {/* Sections List */}
      {sections.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'background.default' }}>
          <RoomIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No hay secciones creadas
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Crea secciones para organizar tu formulario por ubicaciones físicas o etapas del proceso.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Crear Primera Sección
          </Button>
        </Paper>
      ) : (
        <Box>
          {sections.map((section, index) => (
            <Paper key={section.id} sx={{ mb: 2 }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Color indicator */}
                <Box
                  sx={{
                    width: 8,
                    height: 60,
                    bgcolor: section.color || '#2196F3',
                    borderRadius: 1
                  }}
                />

                {/* Order number */}
                <Chip
                  label={`#${index + 1}`}
                  size="small"
                  sx={{
                    bgcolor: section.color || '#2196F3',
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                />

                {/* Section info */}
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {section.name}
                    </Typography>
                    <Chip
                      label="Navegación"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {section.description || 'Sin descripción'}
                  </Typography>
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    title="Mover arriba"
                  >
                    <ArrowUpIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === sections.length - 1}
                    title="Mover abajo"
                  >
                    <ArrowDownIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleOpenDialog(section)}
                    title="Editar"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteSection(section)}
                    title="Eliminar"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSection ? 'Editar Sección' : 'Nueva Sección'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Nombre de la Sección"
              value={dialogData.name}
              onChange={(e) => setDialogData({ ...dialogData, name: e.target.value })}
              error={!!errors.name}
              helperText={errors.name || 'Ejemplo: Cocina, Salón, Exterior, etc.'}
              fullWidth
              required
              autoFocus
            />

            <TextField
              label="Descripción"
              value={dialogData.description}
              onChange={(e) => setDialogData({ ...dialogData, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
              helperText="Descripción opcional de esta sección"
            />

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Color de la sección
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4', '#FFC107', '#795548'].map(
                  color => (
                    <Box
                      key={color}
                      onClick={() => setDialogData({ ...dialogData, color })}
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: color,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: dialogData.color === color ? '3px solid #000' : '1px solid #ddd',
                        '&:hover': {
                          opacity: 0.8
                        }
                      }}
                    />
                  )
                )}
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            Cancelar
          </Button>
          <Button onClick={handleSaveSection} variant="contained">
            {editingSection ? 'Guardar Cambios' : 'Crear Sección'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SectionsStep;
